import { IArgument, ISession } from '@newo/api/gateway';
import { isPlainObject } from '../object';
import { formatSecondsToMinutes } from '../datetime';
import { formatFileSize } from '../file';
/**
 * Convert an array of arguments to an object
 * @param args - Array of arguments
 * @returns Object with arguments
 * */
export const argumentsToObject = (args: IArgument[]) =>
    Object.fromEntries(args.map(({ name, value }) => [name, value]));

/**
 * Transforms a session object into a flat structure suitable for export
 * @param session - The session object to transform
 * @returns An object with human-readable keys containing session data
 */
export const transformSessionForExport = (session: ISession) => {
    return {
        'Date / Time': session.ended_at
            ? new Date(session.ended_at).toLocaleString()
            : '',
        'Session Type': session.session_type ?? session.arguments.category,
        'Is Lead': session.is_lead,
        'Is Opportunity': session.is_opportunity,
        'Is Test': session.is_test,
        Contact: session.persona.name,
        'Contact Information': session.persona.contact_information,
        'External ID': session.persona.external_id,
        Summary: session.arguments.summary ?? '',
        'Lead Value': session.lead_value ?? 0,
        ASR: session.asr ?? 0,
        'Conversation Rate': `${session.conversion_rate * 100}%`,
        'ASR Recognition': `${session.asr_recognition * 100}%`,
        Units: session.units_total ?? 0,
        'Auto Assessment': session.agent_success ?? 'n/a',
        'Human Assessment': session.agent_success_human ?? 'n/a',
        'Conversation Quality': session.conversation_penalty ?? 0,
        'Working Hours': session.arguments.workingHours ?? '',
        Comment: session.comment,
        Channel: session.integration_idn,
        'Actual Result': session.actual_result,
        'Expected Result': session.expected_result
    };
};

/**
 * Counts the number of filters that are active
 * @param filters - The filters object
 * @returns The number of active filters
 */
export const getFiltersCount = <T extends object>(
    filters: T,
    exclude: string[] = []
) => {
    return Object.entries(filters).filter(([key, value]) => {
        if (exclude.includes(key)) {
            return false;
        }
        if (typeof value === 'boolean') {
            return value;
        }
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return value !== undefined && value !== '';
    }).length;
};

/**
 * Processes form data
 * @param formData - The form data to process
 * @param isPatch - Whether the data is a patch
 * @returns The processed data
 */
export const processFormData = <T extends Record<string, unknown>>(
    formData: T,
    isPatch = false
): T => {
    return Object.fromEntries(
        Object.entries(formData).map(([key, value]) => {
            if (typeof value === 'string') {
                return [key, isPatch ? value.trim() || null : value.trim()];
            } else if (isPlainObject(value)) {
                return [key, processFormData(value as T, isPatch)];
            } else {
                return [key, value];
            }
        })
    ) as T;
};

/**
 * Generates a formatted title string for a recording that includes duration and file size if available
 *
 * @param recording - The recording object containing duration and file size information
 * @returns A formatted string with recording title, duration (if available) and file size (if available)
 *
 * @example
 * const recording = { duration: 120, file_size: 1048576 };
 * getRecordingName(recording); // Returns "Recording (2:00) - 1.00 MB"
 *
 * const recordingNoDuration = { file_size: 1048576 };
 * getRecordingName(recordingNoDuration); // Returns "Recording - 1.00 MB"
 */
export const getRecordingName = (
    title: string,
    duration?: number | null,
    fileSize?: number | null
) => {
    if (duration) {
        title += ` (${formatSecondsToMinutes(duration)})`;
    }
    if (fileSize) {
        title += ` - ${formatFileSize(fileSize)}`;
    }
    return title;
};
